import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { arrayOf, bool, func, shape } from 'prop-types';
import * as R from 'ramda';
import { useNavigate } from 'react-router-dom';

import { useGetAffiliateOptions, useUserRolePermissions } from 'hooks';
import { MAutocomplete, MDropdown } from 'common';
import { AuthContext } from 'authentication/AuthContext';
import authorizationTypeOptions from './filterOptions';

const Filters = ({
  classOptions,
  clearButtonDisabled,
  customerOptions,
  destinationOptions,
  fetchCustomerAuthorizations,
  fetchFilterValues,
  fetchingFilterValues,
  filters,
  loadingControlOptions,
  productOptions,
  resetFilters,
  setFilter,
  setMobileFilterDrawerOpen,
  terminalOptions,
}) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const navigate = useNavigate();
  const { currentAffiliate } = useUserRolePermissions();
  const affiliatesOptions = useGetAffiliateOptions(['supplier']);
  const initialRender = useRef(true);
  const [switchState, setSwitchState] = useState('GLOBAL');

  const { affiliates, setCurrentSelectedAffiliate } = useContext(AuthContext);

  useEffect(() => {
    // to prevent fetching on every change of screen size
    if (initialRender.current) {
      initialRender.current = false;
    }
    if (!R.isNil(currentAffiliate) && initialRender) fetchFilterValues(currentAffiliate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAffiliate]);

  const handleAffiliateChange = value => {
    const split = value.split('-');
    const affiliateIndex = affiliates.findIndex(
      ({ customerIdName, dataCompanyId: id }) =>
        split[0] === customerIdName && parseInt(split[1], 10) === id
    );
    const affiliate = affiliates[affiliateIndex];
    resetFilters();
    setCurrentSelectedAffiliate(affiliateIndex);
    navigate(
      `/affiliate/${affiliate.customerIdName}/${affiliate.dataCompanyId}/loading-management/customer-authorizations`,
      {
        replace: true,
      }
    );
  };

  return (
    <Box
      sx={{
        borderRight: 1,
        borderRightColor: 'graphic.light',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100% - 48px)',
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        position: { xs: 'relative', lg: 'fixed' },
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        top: { xs: 'initial', lg: 48 },
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        width: { xs: 1, lg: 360 },
      }}
    >
      <Box
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          },
          backgroundColor: 'surface.white',
          borderLeft: '1px solid',
          borderLeftColor: 'graphic.light',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          gridRowGap: 16,
          overflowY: 'auto',
          pb: 10.5,
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          pt: { xs: 0.5, lg: 2 },
          px: 2,
        }}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
          <Typography sx={{ pt: '2px' }} variant="h6">
            Filters
          </Typography>
          <Button
            disabled={clearButtonDisabled}
            onClick={() => resetFilters()}
            sx={{
              alignSelf: 'flex-end',
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              minWidth: { xs: 95, lg: 112 },
              pl: 1,
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              pr: { xs: 0, lg: 1 },
              py: 0.5,
            }}
            variant="text"
          >
            Clear Filters
          </Button>
        </Box>
        <ToggleButtonGroup
          exclusive
          fullWidth
          onChange={(_, value) => value && setFilter({ name: 'controlType', value })}
          value={filters.controlType}
        >
          <ToggleButton selected={filters.controlType !== 'SINGLE'} value={switchState}>
            multiple term.
          </ToggleButton>
          <ToggleButton value="SINGLE">single term.</ToggleButton>
        </ToggleButtonGroup>
        {filters.controlType !== 'SINGLE' && (
          <FormControlLabel
            control={
              <Switch
                checked={filters.controlType === 'GLOBAL'}
                onChange={({ target }) => {
                  const value = target.checked ? 'GLOBAL' : 'ALL';
                  setFilter({ name: 'controlType', value });
                  setSwitchState(value);
                }}
              />
            }
            label="Share volume across terminals"
          />
        )}
        {affiliatesOptions.length > 1 && (
          <MDropdown
            id="affiliate-select"
            items={affiliatesOptions}
            label="Affiliate"
            loading={fetchingFilterValues}
            name="affiliate"
            onChange={({ value }) => handleAffiliateChange(value)}
            value={`${currentAffiliate?.customerIdName}-${currentAffiliate?.dataCompanyId}`}
          />
        )}
        <MAutocomplete
          fullWidth
          id="terminal-select"
          label="Terminals"
          loading={fetchingFilterValues}
          name="terminalIdName"
          onChange={(_, val, reason) =>
            setFilter({
              name: 'terminalIdName',
              value: reason === 'clear' ? null : R.prop('value', val),
            })
          }
          options={terminalOptions}
          value={filters?.terminalIdName}
        />
        <Box>
          <Typography sx={{ mb: 1 }} variant="body2">
            Terminal Load Status
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            onChange={(_, value) => value && setFilter({ name: 'terminalLoadStatus', value })}
            value={filters.terminalLoadStatus}
          >
            <ToggleButton value="ANY">any</ToggleButton>
            <ToggleButton value="ALLOWED">allowed</ToggleButton>
            <ToggleButton value="NOT ALLOWED">not allowed</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Divider />
        <Box>
          <Typography sx={{ mb: 1 }} variant="body2">
            Authorization Status
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            onChange={(_, value) => value && setFilter({ name: 'authorizationStatus', value })}
            value={filters.authorizationStatus}
          >
            <ToggleButton value="ANY">any</ToggleButton>
            <ToggleButton value="ACTIVE">active</ToggleButton>
            <ToggleButton value="INACTIVE">inactive</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <MAutocomplete
          fullWidth
          id="customer-select"
          label="Customer Name"
          loading={fetchingFilterValues}
          name="customerIdName"
          onChange={(_, val, reason) =>
            setFilter({
              name: 'customerIdName',
              value: reason === 'clear' ? null : R.prop('value', val),
            })
          }
          options={customerOptions}
          value={filters?.customerIdName}
        />
        <MDropdown
          id="authorization-type-select"
          items={authorizationTypeOptions}
          label="Authorization Type"
          loading={fetchingFilterValues}
          name="authorizationType"
          onChange={({ value }) => setFilter({ name: 'authorizationType', value })}
          value={filters?.authorizationType}
        />
        <MAutocomplete
          fullWidth
          id="loading-control-select"
          label="Loading Control ID"
          loading={fetchingFilterValues}
          name="loadingControlId"
          onChange={(_, val, reason) =>
            setFilter({
              name: 'loadingControlId',
              value: reason === 'clear' ? null : R.prop('value', val),
            })
          }
          options={loadingControlOptions}
          value={filters?.loadingControlId}
        />
        <MAutocomplete
          fullWidth
          id="product-select"
          label="Product"
          loading={fetchingFilterValues}
          name="productIdName"
          onChange={(_, val, reason) =>
            setFilter({
              name: 'productIdName',
              value: reason === 'clear' ? null : R.prop('value', val),
            })
          }
          options={productOptions}
          value={filters?.productIdName}
        />
        <MAutocomplete
          fullWidth
          id="class-select"
          label="Class"
          loading={fetchingFilterValues}
          name="customerClassId"
          onChange={(_, val, reason) =>
            setFilter({
              name: 'customerClassId',
              value: reason === 'clear' ? null : R.prop('value', val),
            })
          }
          options={classOptions}
          value={filters?.customerClassId}
        />
        <MAutocomplete
          fullWidth
          id="destination-select"
          label="Destination"
          loading={fetchingFilterValues}
          name="destinationId"
          onChange={(_, val, reason) =>
            setFilter({
              name: 'destinationId',
              value: reason === 'clear' ? null : R.prop('value', val),
            })
          }
          options={destinationOptions}
          value={filters?.destinationId}
        />
      </Box>
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: 'surface.white',
          bottom: 0,
          boxShadow: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          position: 'fixed',
          py: '18px',
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          width: { xs: 'calc(100% - 32px)', lg: 360 },
          zIndex: 1,
        }}
      >
        <Button
          disabled={fetchingFilterValues}
          onClick={() => {
            fetchCustomerAuthorizations(currentAffiliate);
            if (!lgUp) setMobileFilterDrawerOpen(false);
          }}
          sx={{ width: 159 }}
        >
          Search
        </Button>
      </Box>
    </Box>
  );
};

Filters.propTypes = {
  classOptions: arrayOf(shape({})).isRequired,
  clearButtonDisabled: bool.isRequired,
  customerOptions: arrayOf(shape({})).isRequired,
  destinationOptions: arrayOf(shape({})).isRequired,
  fetchCustomerAuthorizations: func.isRequired,
  fetchFilterValues: func.isRequired,
  fetchingFilterValues: bool.isRequired,
  filters: shape({}).isRequired,
  loadingControlOptions: arrayOf(shape({})).isRequired,
  productOptions: arrayOf(shape({})).isRequired,
  resetFilters: func.isRequired,
  setFilter: func.isRequired,
  setMobileFilterDrawerOpen: func.isRequired,
  terminalOptions: arrayOf(shape({})).isRequired,
};

export default Filters;
